import {
  BatchImportReceipt,
  BatchImportRequest,
  callBatchImportQueuePush,
  Configuration,
} from '@/api'
import { zipSync, ZipOptions } from 'fflate'
import DialogUploadValues from '@/types/dialogUploadValues'
import buildNewBatchImport from './buildNewBatchImport'
import { UploadOptions } from './useUploadFile'

const ZIPFILE_NAME = 'upload.zip'

export interface Args {
  values: DialogUploadValues
  useZip?: boolean
  uploadFile: (a: UploadOptions) => Promise<void>
  configurations?: Pick<Configuration, 'cid' | 'groupId'>[]
}

export type BatchImportRequestV2 = Omit<BatchImportRequest, 'files'>

export type HandlePushResult = {
  batchImportID?: string
}

export default async ({
  values,
  uploadFile,
  useZip = false,
  configurations,
}: Args): Promise<HandlePushResult> => {
  const newBatchImport = buildNewBatchImport({ values, dearchived: useZip, configurations })

  if (!useZip) {
    try {
      const result: BatchImportReceipt = await callBatchImportQueuePush(
        newBatchImport
      )
      if (!result?.files) return Promise.reject()

      // Create upload promises for each file
      const uploadPromises = values.files.map((file, index) => {
        return new Promise<void>((resolve, reject) => {
          if (result?.storageLocations && file) {
            uploadFile({
              file,
              signedUrl: result.storageLocations[index],
              batchImportId: result.batchImportID,
            })
              .then(() => resolve())
              .catch((err) => {
                console.error('err', err)
                reject(err)
              })
          }
        })
      })

      await Promise.all(uploadPromises)
      return { batchImportID: result.batchImportID }
    } catch (error) {
      return Promise.reject(error)
    }
  }

  // create the batchImport ONLY - not batchImportFiles
  const result = await callBatchImportQueuePush({
    ...newBatchImport,
    files: [],
  })

  // Prepare a ZIP object: filename -> Uint8Array content
  const zipEntries: Record<string, Uint8Array> = {}

  for (const file of values.files) {
    const arrayBuffer = await file.arrayBuffer()
    const uint8Array = new Uint8Array(arrayBuffer)

    zipEntries[file.name] = uint8Array
  }

  // Create ZIP without compression (store mode) to avoid overhead
  const zipOptions: ZipOptions = {
    level: 0, // 0 = store only, no compression
  }

  // eslint-disable-next-line
  const zipped = zipSync(zipEntries, zipOptions) as BlobPart // why is this type any ?
  const zipBlob = new Blob([zipped], { type: 'application/zip' })
  const zipFile = new File([zipBlob], ZIPFILE_NAME, {
    type: 'application/zip',
  })

  // Prepare FormData or raw body
  const formData = new FormData()
  formData.append('file', zipBlob, ZIPFILE_NAME)

  // NOTE - in the dearchived case we just upload one zip file, containing
  // all the dropped files
  return new Promise<HandlePushResult>((resolve, reject) => {
    if (result?.storageLocations && zipFile) {
      uploadFile({
        file: zipFile,
        signedUrl: result.storageLocations[0],
        batchImportId: result.batchImportID,
      })
        .then(() => resolve({ batchImportID: result.batchImportID }))
        .catch((err) => {
          console.error('err', err)
          reject(err)
        })
    }
  })
}
